import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { Loader } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const C_STATUS = ["Pending", "Filed", "Overdue"];
const RATE = ["High", "Medium", "Low"];
const cTone = (s) => ({ Filed: "green", Pending: "amber", Overdue: "red" }[s] || "gray");
const rTone = (s) => ({ High: "red", Medium: "amber", Low: "green" }[s] || "gray");

export default function ComplianceRisk() {
  const { user } = useAuth();
  const canManage = ["admin", "finance_officer"].includes(user?.role);
  const [items, setItems] = useState(null);
  const [risks, setRisks] = useState([]);
  const [cOpen, setCOpen] = useState(false);
  const [rOpen, setROpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cForm, setCForm] = useState({ requirement: "", category: "IRS / Federal", due_date: "", responsible: "" });
  const [rForm, setRForm] = useState({ title: "", category: "Operational", likelihood: "Medium", impact: "Medium", mitigation: "", owner: "" });

  const load = () => { api.get("/compliance").then((r) => setItems(r.data)); api.get("/risks").then((r) => setRisks(r.data)); };
  useEffect(() => { load(); }, []);

  const addC = async (e) => { e.preventDefault(); setBusy(true);
    try { await api.post("/compliance", cForm); toast.success("Compliance item added"); setCOpen(false); setCForm({ requirement: "", category: "IRS / Federal", due_date: "", responsible: "" }); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); } };
  const addR = async (e) => { e.preventDefault(); setBusy(true);
    try { await api.post("/risks", rForm); toast.success("Risk added"); setROpen(false); setRForm({ title: "", category: "Operational", likelihood: "Medium", impact: "Medium", mitigation: "", owner: "" }); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); } };
  const setC = async (id, status) => { try { await api.patch(`/compliance/${id}`, { status }); toast.success("Updated"); load(); } catch (err) { toast.error(apiError(err)); } };

  if (!items) return <Loader />;

  const cCols = [
    { key: "requirement", label: "Requirement", className: "font-medium" },
    { key: "category", label: "Category", render: (r) => <StatusBadge tone="blue">{r.category}</StatusBadge> },
    { key: "due_date", label: "Due", render: (r) => fmtDate(r.due_date) },
    { key: "responsible", label: "Owner", className: "text-[#5C5C54]" },
    { key: "status", label: "Status", render: (r) => canManage
      ? <select value={r.status} onChange={(e) => setC(r.id, e.target.value)} data-testid={`comp-status-${r.id}`} className="text-xs border border-stone-300 rounded px-2 py-1 bg-white">{C_STATUS.map((s) => <option key={s}>{s}</option>)}</select>
      : <StatusBadge tone={cTone(r.status)}>{r.status}</StatusBadge> },
  ];
  const rCols = [
    { key: "title", label: "Risk", className: "font-medium" },
    { key: "category", label: "Category", className: "text-[#5C5C54]" },
    { key: "likelihood", label: "Likelihood", render: (r) => <StatusBadge tone={rTone(r.likelihood)}>{r.likelihood}</StatusBadge> },
    { key: "impact", label: "Impact", render: (r) => <StatusBadge tone={rTone(r.impact)}>{r.impact}</StatusBadge> },
    { key: "mitigation", label: "Mitigation", className: "text-[#5C5C54] text-xs" },
    { key: "owner", label: "Owner", className: "text-[#5C5C54]" },
  ];

  return (
    <div>
      <PageHeader title="Compliance & Risk" subtitle="Track regulatory filings and maintain the organizational risk register." />
      <Tabs defaultValue="compliance">
        <TabsList className="mb-5">
          <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance Calendar</TabsTrigger>
          <TabsTrigger value="risk" data-testid="tab-risk">Risk Register</TabsTrigger>
        </TabsList>
        <TabsContent value="compliance">
          {canManage && <div className="mb-4 flex justify-end"><AddButton onClick={() => setCOpen(true)} label="Add Requirement" testid="add-comp-btn" /></div>}
          <DataTable columns={cCols} rows={items} testid="compliance-table" />
        </TabsContent>
        <TabsContent value="risk">
          {canManage && <div className="mb-4 flex justify-end"><AddButton onClick={() => setROpen(true)} label="Add Risk" testid="add-risk-btn" /></div>}
          <DataTable columns={rCols} rows={risks} testid="risks-table" />
        </TabsContent>
      </Tabs>

      <Modal open={cOpen} onOpenChange={setCOpen} title="Add Compliance Requirement" onSubmit={addC} busy={busy} testid="comp-modal">
        <Field label="Requirement" span={2}><TextInput required value={cForm.requirement} onChange={(e) => setCForm({ ...cForm, requirement: e.target.value })} /></Field>
        <Field label="Category"><SelectInput value={cForm.category} onChange={(e) => setCForm({ ...cForm, category: e.target.value })} options={["IRS / Federal", "State", "Insurance", "Audit", "Policy"]} /></Field>
        <Field label="Due Date"><TextInput type="date" value={cForm.due_date} onChange={(e) => setCForm({ ...cForm, due_date: e.target.value })} /></Field>
        <Field label="Responsible" span={2}><TextInput value={cForm.responsible} onChange={(e) => setCForm({ ...cForm, responsible: e.target.value })} /></Field>
      </Modal>
      <Modal open={rOpen} onOpenChange={setROpen} title="Add Risk" onSubmit={addR} busy={busy} testid="risk-modal">
        <Field label="Risk Title" span={2}><TextInput required value={rForm.title} onChange={(e) => setRForm({ ...rForm, title: e.target.value })} /></Field>
        <Field label="Category"><SelectInput value={rForm.category} onChange={(e) => setRForm({ ...rForm, category: e.target.value })} options={["Financial", "Operational", "Compliance", "Reputational", "Strategic"]} /></Field>
        <Field label="Owner"><TextInput value={rForm.owner} onChange={(e) => setRForm({ ...rForm, owner: e.target.value })} /></Field>
        <Field label="Likelihood"><SelectInput value={rForm.likelihood} onChange={(e) => setRForm({ ...rForm, likelihood: e.target.value })} options={RATE} /></Field>
        <Field label="Impact"><SelectInput value={rForm.impact} onChange={(e) => setRForm({ ...rForm, impact: e.target.value })} options={RATE} /></Field>
        <Field label="Mitigation" span={2}><TextInput value={rForm.mitigation} onChange={(e) => setRForm({ ...rForm, mitigation: e.target.value })} /></Field>
      </Modal>
    </div>
  );
}
